import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  housePresenceKeys,
  houseService,
  type PresenceImportConfirmItem,
  type PresenceImportPreviewItem,
} from "@/services/houseService";
import { deviceKeys } from "@/services/deviceService";
import { Alert, Button, Input, Select } from "@/components/ui";
import { CountrySelect } from "@/components/ui/phone";
import {
  createActionsColumn,
  type DataTableColumn,
} from "@/components/table";
import {
  ImportSection,
  type ImportRowActions,
  type ImportSectionLabels,
} from "@/components/common/ImportSection";
import { useCountryOptions } from "@/hooks/useCountryOptions";
import { newClientKey } from "@/lib/clientKey";
import { toastApiError } from "@/lib/errorMessages";
import { cn } from "@/lib/utils";

interface AdminHousePresenceImportSectionProps {
  houseId: string;
}

type DraftRow = {
  clientKey: string;
  deviceId: string;
  sourceRoomLabel: string;
  guestName: string;
  nationality: string;
  arrivalDate: string;
  departureDate: string;
};

function emptyRow(nationalityDefault: string): DraftRow {
  return {
    clientKey: newClientKey(),
    deviceId: "",
    sourceRoomLabel: "",
    guestName: "",
    nationality: nationalityDefault,
    arrivalDate: "",
    departureDate: "",
  };
}

function draftToRow(
  item: PresenceImportPreviewItem,
  nationalityDefault: string,
): DraftRow {
  return {
    clientKey: newClientKey(),
    deviceId: item.device_id ?? "",
    sourceRoomLabel: item.source_room_label?.trim() ?? "",
    guestName: item.guest_name ?? "",
    nationality: item.nationality?.trim() || nationalityDefault,
    arrivalDate: item.arrival_date ?? "",
    departureDate: item.departure_date ?? "",
  };
}

function rowIsSavable(row: DraftRow): boolean {
  return (
    Boolean(row.deviceId) &&
    Boolean(row.guestName.trim()) &&
    Boolean(row.nationality.trim()) &&
    Boolean(row.arrivalDate) &&
    Boolean(row.departureDate) &&
    row.departureDate >= row.arrivalDate
  );
}

export function AdminHousePresenceImportSection({
  houseId,
}: AdminHousePresenceImportSectionProps) {
  const { t } = useTranslation("houses");
  const { t: tCommon } = useTranslation("common");
  const { t: tComponents } = useTranslation("components");
  const countryOptions = useCountryOptions();
  const queryClient = useQueryClient();

  const { data: devices = [] } = useQuery({
    queryKey: deviceKeys.byHouse(houseId),
    queryFn: () => houseService.listDevices(houseId),
    enabled: !!houseId,
  });

  const confirmMutation = useMutation({
    mutationFn: (items: PresenceImportConfirmItem[]) =>
      houseService.confirmPresenceImport(houseId, items),
    onSuccess: async (created) => {
      toast.success(
        t("detail.presenceImport.toastSaved", { count: created.length }),
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: housePresenceKeys.current(houseId),
        }),
        queryClient.invalidateQueries({
          queryKey: housePresenceKeys.upcoming(houseId),
        }),
        queryClient.invalidateQueries({
          queryKey: housePresenceKeys.history(houseId),
        }),
        queryClient.invalidateQueries({
          queryKey: deviceKeys.byHouse(houseId),
        }),
        queryClient.invalidateQueries({
          queryKey: ["kiosk-presences", houseId],
        }),
      ]);
    },
    onError: (err) => {
      toastApiError(err, "updateHouse");
    },
  });

  const labels: ImportSectionLabels = {
    title: t("detail.presenceImport.title"),
    barHint: t("detail.presenceImport.barHint"),
    adminBadge: t("detail.presenceImport.adminOnly"),
    open: t("detail.presenceImport.open"),
    modalDescription: t("detail.presenceImport.hint"),
    cancel: t("detail.presenceImport.cancel"),
    close: tCommon("close"),
    extract: t("detail.presenceImport.extract"),
    save: t("detail.presenceImport.save"),
    dropHint: t("detail.presenceImport.dropHint"),
    fileLabel: t("detail.presenceImport.fileLabel"),
    previewCount: (count) =>
      t("detail.presenceImport.previewCount", { count }),
    chooseAnother: t("detail.presenceImport.chooseAnother"),
    emptyPreview: t("detail.presenceImport.emptyPreview"),
    pickFile: t("detail.presenceImport.pickFile"),
    undo: t("detail.presenceImport.undo"),
    discardConfirmTitle: t("detail.presenceImport.discardConfirmTitle"),
    discardConfirmMessage: t("detail.presenceImport.discardConfirmMessage"),
    discardConfirmAction: t("detail.presenceImport.discardConfirmAction"),
    discardConfirmCancel: t("detail.presenceImport.discardConfirmCancel"),
    extractingHint: t("detail.presenceImport.extractingHint"),
    fileSelectedHint: t("detail.presenceImport.fileSelectedHint"),
  };

  const deviceSelectOptions = useMemo(
    () => [
      {
        value: "",
        label: t("detail.presenceImport.devicePlaceholder"),
      },
      ...devices.map((device) => ({
        value: device.id,
        label: device.room_name?.trim() || t("changeRoomNoRoom"),
      })),
    ],
    [devices, t],
  );

  const firstDevice = devices[0];
  if (!firstDevice) {
    return null;
  }
  const houseRegionCode = firstDevice.region_code;

  type PresenceSortColumn = "guest" | "arrival" | "departure";

  const buildColumns = (
    { updateRow, removeRow, isBusy }: ImportRowActions<DraftRow>,
  ): DataTableColumn<DraftRow, PresenceSortColumn>[] => [
    {
      id: "device",
      header: t("detail.presenceImport.colDevice"),
      defaultWidth: 220,
      minWidth: 180,
      cellClassName: "align-middle !py-3",
      cell: (row) => {
        const unmatched = !row.deviceId;
        return (
          <div className="flex items-center gap-2.5">
            <span
              className={cn(
                "h-2.5 w-2.5 shrink-0 rounded-full",
                unmatched ? "bg-error-500" : "bg-success-500",
              )}
              title={
                unmatched
                  ? t("detail.presenceImport.unassignedDeviceTitle")
                  : t("detail.presenceImport.assignedDeviceTitle")
              }
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <Select
                options={deviceSelectOptions}
                value={row.deviceId}
                disabled={isBusy}
                aria-label={t("detail.presenceImport.colDevice")}
                aria-invalid={unmatched}
                onChange={(event) =>
                  updateRow(row.clientKey, {
                    deviceId: event.target.value,
                  })
                }
              />
              {unmatched ? (
                <p className="mt-1 text-xs text-error-700">
                  {row.sourceRoomLabel
                    ? t("detail.presenceImport.unmatchedHint", {
                        room: row.sourceRoomLabel,
                      })
                    : t("detail.presenceImport.unassignedDeviceError")}
                </p>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      id: "guest",
      header: t("detail.presenceImport.colGuest"),
      sortKey: "guest",
      getSortValue: (row) => row.guestName,
      defaultWidth: 200,
      minWidth: 160,
      cellClassName: "align-middle !py-3",
      cell: (row) => (
        <Input
          value={row.guestName}
          disabled={isBusy}
          className="min-w-0"
          aria-label={t("detail.presenceImport.colGuest")}
          onChange={(event) =>
            updateRow(row.clientKey, {
              guestName: event.target.value,
            })
          }
        />
      ),
    },
    {
      id: "nationality",
      header: t("detail.presenceImport.colNationality"),
      defaultWidth: 200,
      minWidth: 170,
      resizable: false,
      cellClassName: "align-middle !py-3 overflow-visible",
      cell: (row) => {
        const selected = devices.find((device) => device.id === row.deviceId);
        return (
        <CountrySelect
          searchPlaceholderLabel={tComponents(
            "countrySelectSearchPlaceholder",
          )}
          searchInputAriaLabel={tComponents(
            "countrySelectSearchPlaceholder",
          )}
          noResultsLabel={tComponents("countrySelectNoResults")}
          value={row.nationality}
          disabled={isBusy}
          className="h-9 rounded-lg px-3 py-1.5 text-sm"
          aria-label={t("detail.presenceImport.colNationality")}
          placeholder={t("kioskPresence.placeholderNationality")}
          countries={countryOptions}
          defaultCountry={
            selected ? selected.region_code : houseRegionCode
          }
          onChange={(value) =>
            updateRow(row.clientKey, {
              nationality: value,
            })
          }
        />
        );
      },
    },
    {
      id: "arrival",
      header: t("detail.presenceImport.colArrival"),
      sortKey: "arrival",
      sortKind: "date",
      getSortValue: (row) =>
        row.arrivalDate ? Date.parse(row.arrivalDate) : null,
      defaultWidth: 190,
      minWidth: 180,
      cellClassName: "align-middle !py-3",
      cell: (row) => {
        const invalidRange =
          Boolean(row.arrivalDate) &&
          Boolean(row.departureDate) &&
          row.departureDate < row.arrivalDate;
        return (
          <Input
            type="date"
            value={row.arrivalDate}
            disabled={isBusy}
            aria-label={t("detail.presenceImport.colArrival")}
            aria-invalid={invalidRange}
            onChange={(event) =>
              updateRow(row.clientKey, {
                arrivalDate: event.target.value,
              })
            }
          />
        );
      },
    },
    {
      id: "departure",
      header: t("detail.presenceImport.colDeparture"),
      sortKey: "departure",
      sortKind: "date",
      getSortValue: (row) =>
        row.departureDate ? Date.parse(row.departureDate) : null,
      defaultWidth: 190,
      minWidth: 180,
      cellClassName: "align-middle !py-3",
      cell: (row) => {
        const invalidRange =
          Boolean(row.arrivalDate) &&
          Boolean(row.departureDate) &&
          row.departureDate < row.arrivalDate;
        return (
          <div className="space-y-1">
            <Input
              type="date"
              value={row.departureDate}
              disabled={isBusy}
              aria-label={t("detail.presenceImport.colDeparture")}
              aria-invalid={invalidRange}
              onChange={(event) =>
                updateRow(row.clientKey, {
                  departureDate: event.target.value,
                })
              }
            />
            {invalidRange ? (
              <p className="text-xs font-medium text-error-600">
                {t("detail.presenceImport.invalidDateRange")}
              </p>
            ) : null}
          </div>
        );
      },
    },
    createActionsColumn("", {
      defaultWidth: 56,
      minWidth: 56,
      cellClassName: "align-middle !py-3 px-3 text-center",
      cell: (row) => (
        <div className="flex h-9 items-center justify-center">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-error-600"
            disabled={isBusy}
            aria-label={t("detail.presenceImport.removeRow")}
            onClick={() => removeRow(row.clientKey)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    }),
  ];

  return (
    <ImportSection<DraftRow, PresenceSortColumn>
      entityKey={`presence-${houseId}`}
      preferencesKey="house-presence-import-v5"
      defaultSortColumn="arrival"
      modalSize="xl"
      previewModalSize="full"
      labels={labels}
      buildColumns={buildColumns}
      canSave={(rows) => rows.length > 0 && rows.every(rowIsSavable)}
      rowClassName={(row) =>
        !row.deviceId ? "bg-error-50/60" : undefined
      }
      previewTopContent={({ rows }) => {
        const unassignedCount = rows.filter((row) => !row.deviceId).length;
        return (
          <div className="space-y-2">
            {unassignedCount > 0 ? (
              <Alert variant="error" className="rounded-lg px-3 py-2">
                {t("detail.presenceImport.unassignedBanner", {
                  count: unassignedCount,
                })}
              </Alert>
            ) : null}
            <p className="text-xs text-surface-500">
              {t("detail.presenceImport.scheduleHint")}
            </p>
          </div>
        );
      }}
      onExtract={async (file) => {
        const preview = await houseService.extractPresencesFromPdf(houseId, file);
        return preview.items.map((item) => {
          const matched = item.device_id
            ? devices.find((device) => device.id === item.device_id)
            : undefined;
          return draftToRow(
            item,
            matched ? matched.region_code : houseRegionCode,
          );
        });
      }}
      onExtractSuccess={(rows) => {
        toast.success(
          t("detail.presenceImport.toastExtracted", { count: rows.length }),
        );
      }}
      onExtractError={(err) => toastApiError(err, "updateHouse")}
      onConfirm={async (rows) => {
        const items: PresenceImportConfirmItem[] = rows.map((row) => ({
          device_id: row.deviceId,
          guest_name: row.guestName.trim(),
          nationality: row.nationality.trim(),
          arrival_date: row.arrivalDate,
          departure_date: row.departureDate,
        }));
        await confirmMutation.mutateAsync(items);
      }}
      onConfirmError={(err) => toastApiError(err, "updateHouse")}
      previewToolbarTrailing={({ isBusy, appendRow }) => (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={isBusy}
          onClick={() => appendRow(emptyRow(houseRegionCode))}
        >
          <Plus className="mr-1 h-4 w-4" />
          {t("detail.presenceImport.addRow")}
        </Button>
      )}
    />
  );
}
